import { useMemo } from "react";
import { CheckCircle2, FileText, XCircle } from "lucide-react";
import { cn } from "@ncp/ui";
import { evaluate } from "./conditions";
import { enumOptionsOf, getAtPath, labelOf, localize, primaryType, schemaAtPath } from "./schema";
import type { FormContext, FormDocument, FormLocale, PropertySchema, UiField, UploadedFile } from "./types";
import { ensureBuiltInWidgets } from "./widgets";
import { getWidget, resolveWidgetKey, widgetOptions, type WidgetProps } from "./widgets/registry";
import type { DocumentEntry, DocumentSpec } from "./widgets/documents";
import type { TableColumn } from "./widgets/composite";

const T = {
  yes: { en: "Yes", ar: "نعم" },
  no: { en: "No", ar: "لا" },
  none: { en: "—", ar: "—" },
  items: { en: (n: number) => `${n} item${n === 1 ? "" : "s"}`, ar: (n: number) => `${n} عنصر` },
  hidden: { en: "Restricted", ar: "مقيد" },
};

function fmtNumber(v: number, locale: FormLocale, schema: PropertySchema, options: Record<string, unknown>, ctx: FormContext): string {
  const f = new Intl.NumberFormat(locale === "ar" ? "ar-AE" : "en-AE", { maximumFractionDigits: schema.type === "integer" ? 0 : 2 });
  if (schema.format === "currency") {
    const cur = (options.currency as string | undefined) ?? "AED";
    return ctx.formatCurrency ? ctx.formatCurrency(v, cur) : `${f.format(v)} ${cur}`;
  }
  if (schema.format === "percent") return `${f.format(v)}%`;
  return `${f.format(v)}${schema["x-ncp"]?.unit ? ` ${schema["x-ncp"]?.unit}` : ""}`;
}

function fmtDate(v: string, locale: FormLocale, withTime: boolean): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-AE" : "en-GB", withTime ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "medium" }).format(d);
}

/** Formats any value for display according to its schema; composite widgets get their own table/list rendering. */
export function ReadOnlyValue({ path, schema, value, locale, context, ui, maskSensitive }: { path: string; schema: PropertySchema; value: unknown; locale: FormLocale; context: FormContext; ui?: UiField; maskSensitive?: boolean }) {
  ensureBuiltInWidgets();
  if (maskSensitive && schema["x-ncp"]?.sensitive) return <span className="text-muted-foreground">••••• <span className="text-[11px]">({localize(T.hidden, locale)})</span></span>;
  if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) return <span className="text-muted-foreground">{localize(T.none, locale)}</span>;
  const key = resolveWidgetKey(schema, ui);
  const widget = getWidget(key);
  const options = widgetOptions(schema, ui);
  if (widget?.readOnly) {
    const RO = widget.readOnly;
    const props: WidgetProps = { id: `ro-${path}`, path, schema, ui: ui ?? { path }, value, onChange: () => undefined, onBlur: () => undefined, required: false, disabled: true, readOnly: true, invalid: false, locale, context, options, label: labelOf(schema, path, locale) };
    return <RO {...props} />;
  }
  switch (key) {
    case "party":
      return <PartyView value={value as Record<string, unknown>} locale={locale} />;
    case "partyList":
      return <div className="space-y-2">{(value as Array<Record<string, unknown>>).map((p, i) => <PartyView key={i} value={p} locale={locale} />)}</div>;
    case "table":
    case "turnoverTable":
    case "marketTable":
      return <TableView rows={value as Array<Record<string, unknown>>} schema={schema} options={options} locale={locale} context={context} />;
    case "documentChecklist":
      return <ChecklistView entries={value as DocumentEntry[]} docs={(options.documents as DocumentSpec[] | undefined) ?? []} locale={locale} />;
    case "file":
      return <FileChip file={value as UploadedFile} />;
    case "fileList":
      return <div className="flex flex-wrap gap-1.5">{(value as UploadedFile[]).map((f, i) => <FileChip key={i} file={f} />)}</div>;
    default:
      break;
  }
  const type = primaryType(schema);
  if (type === "boolean") return <span className={cn("inline-flex items-center gap-1", value ? "text-success-700" : "text-muted-foreground")}>{value ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />} {localize(value ? T.yes : T.no, locale)}</span>;
  if (type === "number" || type === "integer") return <span className="tabular-nums" dir="ltr">{fmtNumber(Number(value), locale, schema, options, context)}</span>;
  if (schema.enum || schema["x-ncp"]?.enumOptions) {
    const o = enumOptionsOf(schema, locale).find((x) => x.value === String(value));
    return <>{o?.label ?? String(value)}</>;
  }
  if (type === "array") {
    const items = value as unknown[];
    if (schema.items?.enum || schema.items?.["x-ncp"]?.enumOptions) {
      const opts = enumOptionsOf(schema.items, locale);
      return <div className="flex flex-wrap gap-1">{items.map((v, i) => <span key={i} className="rounded-md bg-sand-100 px-2 py-0.5 text-xs">{opts.find((o) => o.value === String(v))?.label ?? String(v)}</span>)}</div>;
    }
    if (schema.items?.properties) {
      return (
        <div className="space-y-2">
          {items.map((it, i) => (
            <dl key={i} className="grid gap-x-4 gap-y-1 rounded-lg border border-sand-200 bg-white/60 p-3 sm:grid-cols-2">
              {Object.entries(schema.items!.properties!).map(([k, child]) => (
                <div key={k} className="min-w-0">
                  <dt className="text-[11px] uppercase tracking-wider text-sand-600">{labelOf(child, k, locale)}</dt>
                  <dd className="text-sm"><ReadOnlyValue path={`${path}.${i}.${k}`} schema={child} value={(it as Record<string, unknown>)?.[k]} locale={locale} context={context} /></dd>
                </div>
              ))}
            </dl>
          ))}
        </div>
      );
    }
    return <>{items.map(String).join(", ")}</>;
  }
  if (type === "object" && schema.properties) {
    return (
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
        {Object.entries(schema.properties).map(([k, child]) => (
          <div key={k} className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wider text-sand-600">{labelOf(child, k, locale)}</dt>
            <dd className="text-sm"><ReadOnlyValue path={`${path}.${k}`} schema={child} value={(value as Record<string, unknown>)?.[k]} locale={locale} context={context} /></dd>
          </div>
        ))}
      </dl>
    );
  }
  if (schema.format === "date") return <span dir="ltr">{fmtDate(String(value), locale, false)}</span>;
  if (schema.format === "date-time") return <span dir="ltr">{fmtDate(String(value), locale, true)}</span>;
  if (schema.format === "email") return <a href={`mailto:${String(value)}`} className="underline-offset-2 hover:underline" dir="ltr">{String(value)}</a>;
  if (schema.format === "uri") return <a href={String(value)} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" dir="ltr">{String(value)}</a>;
  const text = String(value);
  return text.length > 240 || text.includes("\n") ? <p className="whitespace-pre-wrap text-sm">{text}</p> : <>{text}</>;
}

function PartyView({ value, locale }: { value: Record<string, unknown>; locale: FormLocale }) {
  const name = locale === "ar" && value.nameAr ? String(value.nameAr) : String(value.name ?? "");
  return (
    <div className="rounded-lg border border-sand-200 bg-white/60 px-3 py-2 text-sm">
      <p className="font-medium">{name} {value.role ? <span className="ms-1 rounded bg-sand-100 px-1.5 py-0.5 text-[11px] font-normal text-sand-700">{String(value.role)}</span> : null} {value.isSubmitter ? <span className="ms-1 rounded bg-aegold-50 px-1.5 py-0.5 text-[11px] font-normal text-aegold-800">{locale === "ar" ? "مقدم الطلب" : "Submitter"}</span> : null}</p>
      <p className="text-xs text-muted-foreground" dir="ltr">{[value.registryNumber, value.emirate, value.jurisdiction, value.contactEmail].filter(Boolean).join(" · ")}</p>
    </div>
  );
}

function TableView({ rows, schema, options, locale, context }: { rows: Array<Record<string, unknown>>; schema: PropertySchema; options: Record<string, unknown>; locale: FormLocale; context: FormContext }) {
  const columns = useMemo<TableColumn[]>(() => (options.columns as TableColumn[] | undefined) ?? Object.entries(schema.items?.properties ?? {}).map(([key, p]) => ({ key, label: labelOf(p, key, locale), total: p["x-ncp"]?.options?.total === true, type: p.format === "currency" ? "currency" : p.format === "percent" ? "percent" : p.type === "number" || p.type === "integer" ? "number" : "text" })), [options.columns, schema, locale]);
  return (
    <div className="overflow-x-auto rounded-lg border border-sand-200">
      <table className="w-full text-sm">
        <thead className="bg-sand-50 text-[11px] uppercase tracking-wider text-sand-700">
          <tr>{columns.map((c) => <th key={c.key} className="px-3 py-1.5 text-start font-semibold">{localize(c.label, locale)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-sand-100">
              {columns.map((c) => {
                const child = schema.items?.properties?.[c.key] ?? { type: c.type === "text" ? "string" : "number", format: c.type === "currency" || c.type === "percent" ? c.type : undefined };
                return <td key={c.key} className={cn("px-3 py-1.5", c.type !== "text" && c.type !== "select" && "text-end tabular-nums")}><ReadOnlyValue path={`${i}.${c.key}`} schema={child} value={r[c.key]} locale={locale} context={context} /></td>;
              })}
            </tr>
          ))}
        </tbody>
        {columns.some((c) => c.total) ? (
          <tfoot className="border-t border-sand-200 bg-sand-50/60 font-semibold">
            <tr>{columns.map((c, idx) => <td key={c.key} className={cn("px-3 py-1.5", c.total && "text-end tabular-nums")}>{c.total ? <ReadOnlyValue path={`total.${c.key}`} schema={schema.items?.properties?.[c.key] ?? { type: "number" }} value={rows.reduce((a, r) => a + (Number(r[c.key]) || 0), 0)} locale={locale} context={context} /> : idx === 0 ? (locale === "ar" ? "الإجمالي" : "Total") : ""}</td>)}</tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function ChecklistView({ entries, docs, locale }: { entries: DocumentEntry[]; docs: DocumentSpec[]; locale: FormLocale }) {
  const keys = new Set([...docs.map((d) => d.key), ...entries.map((e) => e.documentKey)]);
  return (
    <ul className="space-y-1.5">
      {[...keys].map((k) => {
        const spec = docs.find((d) => d.key === k);
        const files = entries.filter((e) => e.documentKey === k);
        return (
          <li key={k} className="flex flex-wrap items-center gap-2 text-sm">
            {files.length ? <CheckCircle2 className="size-4 text-success-600" /> : <XCircle className={cn("size-4", spec?.required ? "text-danger-500" : "text-sand-400")} />}
            <span className={cn(!files.length && "text-muted-foreground")}>{spec ? localize(spec.name, locale) : k}</span>
            {files.map((f, i) => <FileChip key={i} file={f} />)}
          </li>
        );
      })}
    </ul>
  );
}

function FileChip({ file }: { file: UploadedFile }) {
  const inner = <><FileText className="size-3.5 text-aegold-700" /> <span className="max-w-[16rem] truncate" dir="ltr">{file.name}</span></>;
  return file.url ? <a href={file.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-sand-200 bg-white px-2 py-0.5 text-xs hover:border-aegold-300">{inner}</a> : <span className="inline-flex items-center gap-1 rounded-md border border-sand-200 bg-white px-2 py-0.5 text-xs">{inner}</span>;
}

export interface FormReadOnlyProps {
  document: FormDocument;
  value: Record<string, unknown> | null | undefined;
  locale?: FormLocale;
  context?: Partial<FormContext>;
  /** Hide `x-ncp.sensitive` values. */
  maskSensitive?: boolean;
  /** Render step headings (default true). */
  showSteps?: boolean;
  className?: string;
  /** Skip empty values. */
  hideEmpty?: boolean;
}

/** Read-only summary of a submission organised by the UI schema's steps and sections (back-office "Application data" panel). */
export function FormReadOnly({ document, value, locale = "en", context, maskSensitive, showSteps = true, className, hideEmpty }: FormReadOnlyProps) {
  ensureBuiltInWidgets();
  const data = value ?? {};
  const ctx: FormContext = { locale, dir: locale === "ar" ? "rtl" : "ltr", ...context };
  const steps = document.uiSchema.steps.filter((s) => !s.review && evaluate(s.visibleWhen, data));
  return (
    <div dir={ctx.dir} className={cn("space-y-6", locale === "ar" && "font-arabic", className)}>
      {steps.map((step) => (
        <section key={step.key} className="space-y-4">
          {showSteps ? <h3 className="text-base font-semibold">{localize(step.title, locale)}</h3> : null}
          {step.sections.filter((sec) => evaluate(sec.visibleWhen, data)).map((sec) => {
            const fields = sec.fields.filter((f) => !f.content && evaluate(f.visibleWhen, data)).filter((f) => !hideEmpty || !isEmpty(getAtPath(data, f.path)));
            if (fields.length === 0) return null;
            return (
              <div key={sec.key} className={cn(sec.title && "rounded-xl border border-sand-200 bg-white/50 p-4")}>
                {sec.title ? <h4 className="mb-3 text-sm font-semibold text-aeblack-900">{localize(sec.title, locale)}</h4> : null}
                <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                  {fields.map((f) => {
                    const node = schemaAtPath(document.schema, f.path);
                    if (!node) return null;
                    const wide = (getWidget(resolveWidgetKey(node, f))?.defaultSpan ?? 6) >= 12 || primaryType(node) === "array" || (primaryType(node) === "object" && !node["x-ncp"]?.widget);
                    return (
                      <div key={f.path} className={cn("min-w-0", wide && "sm:col-span-2")}>
                        <dt className="text-xs uppercase tracking-wider text-sand-600">{labelOf(node, f.path, locale)}</dt>
                        <dd className="mt-0.5 text-sm font-medium text-foreground"><ReadOnlyValue path={f.path} schema={node} value={getAtPath(data, f.path)} locale={locale} context={ctx} ui={f} maskSensitive={maskSensitive} /></dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) || (typeof v === "object" && v !== null && Object.keys(v).length === 0);
}
